/**
 * What the web inbox does with an AI draft (Draft mode). The draft waits on its
 * own card above the composer: it is never typed into the composer for staff,
 * and Use asks before it replaces something a person typed. Kept free of React
 * so the rules can be tested with node --test.
 */

export interface AiDraftLike {
  _id?: string | null;
  draftId?: string | null;
  draftText?: string | null;
  aiMeta?: {
    // What the AI wants the team to know or check; never meant for the customer.
    staffNote?: string | null;
    // When the website pages behind the draft were read (ISO).
    websiteCheckedAt?: string | null;
    sources?: unknown;
  } | null;
  createdAt?: string | Date | null;
}

// A price or stock level read from the website this long ago may have changed.
export const WEBSITE_RECHECK_AFTER_MS = 60 * 60 * 1000;

export const aiDraftId = (draft?: AiDraftLike | null): string | null =>
  draft?.draftId || draft?._id || null;

/** The reply the AI wrote, or '' when it only left a note for the team. */
export function draftReplyText(draft?: AiDraftLike | null): string {
  const text = typeof draft?.draftText === 'string' ? draft.draftText : '';
  return text.trim() ? text : '';
}

/** The AI's note for the team (what it could not answer or what to check), or ''. */
export function draftStaffNote(draft?: AiDraftLike | null): string {
  const note = draft?.aiMeta?.staffNote;
  return typeof note === 'string' ? note.trim() : '';
}

/** A draft is worth showing when it has a reply, a note for the team, or both. */
export const hasAiDraftContent = (draft?: AiDraftLike | null): boolean =>
  Boolean(draftReplyText(draft) || draftStaffNote(draft));

/**
 * The conversation list's chip: 'draft' for a reply waiting on its card,
 * 'needs_you' when the AI only left a note because it could not answer.
 */
export function aiDraftChip(draft?: AiDraftLike | null): 'draft' | 'needs_you' | null {
  if (draftReplyText(draft)) return 'draft';
  return draftStaffNote(draft) ? 'needs_you' : null;
}

/**
 * The composer when a conversation opens: only what this person typed (saved
 * per conversation) comes back. A draft stays on its card. Saved text counts as
 * the draft in use only when Use left its marker for this draft (usedDraftId).
 * The draft's own text without that marker was typed in by the inbox before
 * drafts moved to the card, so it is dropped and the composer opens empty.
 */
export function composerOnOpen(
  savedMessage: string | null | undefined,
  draft?: AiDraftLike | null,
  usedDraftId?: string | null,
): { message: string; activeAiDraftId: string | null } {
  const message = savedMessage || '';
  const id = aiDraftId(draft);
  if (message && id && usedDraftId === id) return { message, activeAiDraftId: id };
  const text = draftReplyText(draft);
  if (message && text && message === text) return { message: '', activeAiDraftId: null };
  return { message, activeAiDraftId: null };
}

/**
 * Where the inbox keeps a conversation's unsent composer text, and the marker
 * Use writes next to it (the id of the draft it put there). Both live in
 * localStorage, which can be missing or throw (private windows, blocked storage).
 */
export type ComposerStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
export const savedMessageKey = (conversationId: string) => `message-${conversationId}`;
export const usedDraftKey = (conversationId: string) => `message-${conversationId}-aiDraft`;

function readStored(storage: ComposerStorage | null | undefined, key: string): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function writeStored(storage: ComposerStorage | null | undefined, key: string, value: string) {
  try {
    storage?.setItem(key, value);
  } catch {
    /* no-op */
  }
}

function removeStored(storage: ComposerStorage | null | undefined, key: string) {
  try {
    storage?.removeItem(key);
  } catch {
    /* no-op */
  }
}

/**
 * composerOnOpen over what this browser saved for the conversation. A draft the
 * old inbox typed in by itself is also removed from storage, so it cannot come
 * back as typed text once the draft is gone.
 */
export function restoreComposer(
  storage: ComposerStorage | null | undefined,
  conversationId: string,
  draft?: AiDraftLike | null,
): { message: string; activeAiDraftId: string | null } {
  const saved = readStored(storage, savedMessageKey(conversationId));
  const opened = composerOnOpen(saved, draft, readStored(storage, usedDraftKey(conversationId)));
  if (saved && !opened.message) removeStored(storage, savedMessageKey(conversationId));
  return opened;
}

/**
 * Use: the draft goes into the composer and is saved there with the marker
 * that says Use put it there. Null when the draft has no reply to use.
 */
export function putDraftInComposer(
  storage: ComposerStorage | null | undefined,
  conversationId: string,
  draft?: AiDraftLike | null,
): { message: string; activeAiDraftId: string | null } | null {
  const message = draftReplyText(draft);
  if (!message) return null;
  const id = aiDraftId(draft);
  writeStored(storage, savedMessageKey(conversationId), message);
  if (id) writeStored(storage, usedDraftKey(conversationId), id);
  else removeStored(storage, usedDraftKey(conversationId));
  return { message, activeAiDraftId: id };
}

/** The composer was cleared, the draft dismissed, or the reply sent: Use no longer applies. */
export function forgetUsedDraft(
  storage: ComposerStorage | null | undefined,
  conversationId: string,
) {
  removeStored(storage, usedDraftKey(conversationId));
}

/** Why the team dismissed a draft, sent to webchat as a code (it drops anything else). */
export const DISMISS_REASONS = [
  { code: 'no_reply_needed', label: 'No reply needed' },
  { code: 'wrong_or_unsure', label: "Wrong, or I'm not sure it's right" },
  { code: 'handled_myself', label: 'I handled it myself' },
  { code: 'other', label: 'Something else' },
] as const;
export type DismissReason = (typeof DISMISS_REASONS)[number]['code'];
export const isDismissReason = (value: unknown): value is DismissReason =>
  DISMISS_REASONS.some((reason) => reason.code === value);

export interface DismissRequestBody {
  _customerConversation: string;
  _business?: string;
  aiDraftId: string | null;
  reason?: DismissReason;
}

/**
 * Dismiss from the card (ChatBox, and the Playwright harness through the same
 * function). The card goes only once webchat recorded the dismissal: a request
 * that fails and the route's own `{ error: true }` answer (sent with a 200) both
 * keep it. After a dismissal, a Use is forgotten, and the composer is cleared
 * when it still holds the draft exactly as the AI wrote it.
 */
export async function dismissDraftFromCard({
  post,
  storage,
  conversationId,
  businessId,
  draft,
  reason,
  composerText,
}: {
  post: (body: DismissRequestBody) => Promise<{ data?: unknown } | null | undefined>;
  storage: ComposerStorage | null | undefined;
  conversationId: string;
  businessId?: string | null;
  draft: AiDraftLike | null | undefined;
  reason?: unknown;
  composerText: string | null | undefined;
}): Promise<{ dismissed: boolean; clearComposer: boolean }> {
  const body: DismissRequestBody = {
    _customerConversation: conversationId,
    aiDraftId: aiDraftId(draft),
  };
  if (businessId) body._business = businessId;
  if (isDismissReason(reason)) body.reason = reason;
  try {
    const res = await post(body);
    const data = res?.data as { error?: unknown } | null | undefined;
    if (data && typeof data === 'object' && data.error) {
      return { dismissed: false, clearComposer: false };
    }
  } catch {
    return { dismissed: false, clearComposer: false };
  }

  forgetUsedDraft(storage, conversationId);
  const text = draftReplyText(draft);
  const clearComposer = Boolean(text) && composerText === text;
  if (clearComposer && readStored(storage, savedMessageKey(conversationId)) === text) {
    removeStored(storage, savedMessageKey(conversationId));
  }
  return { dismissed: true, clearComposer };
}

// rep's /api/ai/generate-response answers this when the ai-employee call failed.
const GENERATE_RESPONSE_FAILED = 'error generating response';

/**
 * The inbox "Generate response" answer as Messages.jsx passes it on: the reply
 * ('' when the AI could not answer) and its note for the team, or null when
 * there is neither (an error).
 */
export function generatedResponseFrom(
  data: unknown,
): { response: string; staffNote?: string } | null {
  const body = (data && typeof data === 'object' ? data : {}) as {
    response?: unknown;
    staffNote?: unknown;
  };
  const response =
    typeof body.response === 'string' && body.response !== GENERATE_RESPONSE_FAILED
      ? body.response
      : '';
  const staffNote = typeof body.staffNote === 'string' ? body.staffNote.trim() : '';
  if (!response.trim() && !staffNote) return null;
  return staffNote ? { response, staffNote } : { response };
}

/**
 * What the Generate response button does with that answer: type the reply (and
 * show the note after it), show only the note, or report an error.
 */
export function generatedResponseAction(
  result: { response?: unknown; staffNote?: unknown } | null | undefined,
):
  | { kind: 'reply'; text: string; note: string }
  | { kind: 'note'; note: string }
  | { kind: 'error' } {
  const text =
    typeof result?.response === 'string' && result.response !== 'error' ? result.response : '';
  const note = typeof result?.staffNote === 'string' ? result.staffNote.trim() : '';
  if (text.trim()) return { kind: 'reply', text, note };
  return note ? { kind: 'note', note } : { kind: 'error' };
}

/** Use only asks first when it would replace words a person typed. */
export function wouldReplaceTypedText(composerText: string | null | undefined, draftText: string) {
  const typed = composerText || '';
  return Boolean(typed.trim()) && typed !== draftText;
}

/**
 * The draft is "in the composer" once Use put it there and the composer still
 * holds something; clearing the composer brings the full card back.
 */
export function isDraftInComposer(
  draft: AiDraftLike | null | undefined,
  activeAiDraftId: string | null | undefined,
  composerText: string | null | undefined,
): boolean {
  const id = aiDraftId(draft);
  return Boolean(id) && activeAiDraftId === id && Boolean((composerText || '').trim());
}

/** "just now", "4 min ago", "3 h ago", "yesterday", "5 days ago"; '' when unknown. */
export function timeAgo(at: string | Date | null | undefined, now: number = Date.now()): string {
  if (!at) return '';
  const time = new Date(at).getTime();
  if (Number.isNaN(time)) return '';
  const minutes = Math.floor(Math.max(0, now - time) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

/** True when the website facts behind a draft are old enough to look at again before sending. */
export function websiteCheckIsOld(
  checkedAt: string | Date | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!checkedAt) return false;
  const time = new Date(checkedAt).getTime();
  return !Number.isNaN(time) && now - time > WEBSITE_RECHECK_AFTER_MS;
}
